import {
  Color, GridImageryProvider,
  ImageryProvider,
  OpenStreetMapImageryProvider,
  UrlTemplateImageryProvider
} from 'cesium';
import {useEffect, useState} from 'react';
import {ImageryLayer} from 'resium';

import unreachable from '../../utils/unreachable';

import {GlobusImage, useGlobus} from './GlobusProvider';

const HERE_API_KEY = 'iGpbhAEqPP5PYILRcynUge_VpaLlNu7SSUAqcCphZoA';

type Props = {image: GlobusImage};

async function getImageryProvider(image: GlobusImage): Promise<ImageryProvider> {

  switch (image) {
    case 'sat':
      // Provide a custom URL to support Here Maps.
      // https://cesium.com/learn/cesiumjs/ref-doc/UrlTemplateImageryProvider.html
      // https://developer.here.com/documentation/map-tile/dev_guide/topics/example-satellite-map.html
      // https://2.aerial.maps.ls.hereapi.com/maptile/2.1/maptile/newest/satellite.day/5/15/12/256/png8?apiKey={YOUR_API_KEY}
      // 'https://2.aerial.maps.ls.hereapi.com/maptile/2.1/maptile/newest/satellite.day/{z}/{x}/{y}/256/jpg?apiKey=' +
      // 'https://maps.hereapi.com/v3/raster/tiles/{z}/{x}/{y}/satellite?apikey=' + HERE_API_KEY

      return new UrlTemplateImageryProvider({
        // Set size of the requested tile to 512 (default 256) while setting the [tileWidth] and [tileHeight] to 64
        // (default 265), to switch to higher z-levels when zooming in and better tile quality. Setting
        // [resolutionScale] on the [Viewer] did not work and setting [maximumScreenSpaceError] on the globe did not
        // work either. Feels like cheating, but it works somehow?!
        //
        // Raster Tile Api v3:
        //     https://www.here.com/docs/bundle/raster-tile-api-v3-api-reference/page/index.html
        // Resource Info:
        //     https://maps.hereapi.com/v3/info?apikey=${HERE_API_KEY}
        // Playground:
        //     https://www.here.com/docs/bundle/raster-tile-api-developer-guide/page/topics/playground/playground.html
        url: `https://maps.hereapi.com/v3/background/mc/{z}/{x}/{y}/jpeg` +
          `?apikey=${HERE_API_KEY}` +
          `&style=satellite.day` +
          `&size=512`, // Size of the requested tile
        tileWidth: 64,
        tileHeight: 64,
        minimumLevel: 0, // Here Maps min zoom level (see info resource above)
        maximumLevel: 20, // Here Maps max zoom level (see info resource above)
        credit: '© Here Maps',
      });

    case 'phys':
      return new OpenStreetMapImageryProvider({
        url: 'https://globusserver.de/cgi-bin/ImagonTileCache2.pl?mergephysical/',
        maximumLevel: 12,
        fileExtension: 'jpg',
      });

    case 'osm':
      return new OpenStreetMapImageryProvider({
        url: 'https://b.tile.openstreetmap.org/',
      });

    case 'dierckeRoads':
      return new OpenStreetMapImageryProvider({
        url: 'https://globusserver.de/cgi-bin/ImagonTileCache2.pl?osmroads/',
      });

    case 'dierckeBorders':
      return new OpenStreetMapImageryProvider({
        url: 'https://globusserver.de/cgi-bin/ImagonTileCache2.pl?osmborders/',
      });

    case 'grid':
      return new GridImageryProvider({
        tileWidth: 2048,
        tileHeight: 2048,
        color: new Color(0.0, 1.0, 1.0, 0.1),
      });

    default:
      unreachable(image);
  }
}


export const TileImageryProvider: React.FC<Props> = ({image}) => {
  const {image: selectedImage, bordersEnabled} = useGlobus();
  const [imageryProvider, setImageryProvider] = useState<ImageryProvider | null>(null);
  const [imageryProviderBorders, setImageryProviderBorders] = useState<ImageryProvider | null>(null);

  useEffect(() => {
    let mounted = true;
    const fetchImageryProviders = async () => {
      const provider = await getImageryProvider(image);  // Fetch the imagery for the selected image
      const providerBorder = await getImageryProvider('dierckeBorders');  // Fetch borders imagery

      if (mounted) {
        setImageryProvider(provider);
        setImageryProviderBorders(providerBorder);
      }
    };

    fetchImageryProviders().catch(console.error);
    return () => {
      mounted = false;
    };
  }, [image]); // Re-run the effect when the image changes

  if (!imageryProvider || !imageryProviderBorders) {
    return null; // Return null if no imagery provider is set yet
  }

  return (
    <>
      <ImageryLayer imageryProvider={imageryProvider} show={selectedImage === image}/>
      {bordersEnabled && (
        <ImageryLayer imageryProvider={imageryProviderBorders} show={true}/>
      )}
    </>
  );
};
